import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import ModelImageSelection, { exampleModelImages, handleExampleModelClick } from './ModelImageSelection';
import CustomBackgroundUpload from './CustomBackgroundUpload';
import { useAuth } from '../../context/AuthContext';
import API_CONFIG from '../../apiConfig';
import { backgroundChangeExamplePresets, BackgroundChangeExamplePreset } from './examplePresets';
import { useCreditEstimation } from '../../hooks/useCreditEstimation';
import { useModeRestrictions } from '../../hooks/useModeRestrictions';
import CreditCostPreview from '../ui/CreditCostPreview';
import InsufficientCreditsModal from '../ui/InsufficientCreditsModal';
import CustomSelect from '../ui/CustomSelect';

interface LocationState {
    inputImages: string[];
    category: string;
    mode: string;
    recreatingFrom: string;
    generatedImages?: string[];
}

// Background category options
const BACKGROUND_CATEGORIES = [
    { value: 'random', label: 'All', description: 'Show all background categories' },
    { value: 'classic', label: 'Classic', description: 'Timeless and elegant backgrounds' },
    { value: 'luxury', label: 'Luxury', description: 'Premium and sophisticated settings' },
    { value: 'office', label: 'Office', description: 'Professional office environments' },
    { value: 'mahal', label: 'Mahal', description: 'Palace and grand architecture' },
    { value: 'outdoor', label: 'Outdoor', description: 'Natural outdoor settings' },
    { value: 'indoor', label: 'Indoor', description: 'Home interior settings' }
];

// Background preset interface
interface BackgroundPreset {
    backgroundId: string;
    imageUrl: string;
    category: string;
    timestamp: string;
    liveStatus: boolean;
}

const AIBackgroundChange: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const state = location.state as LocationState;

    const [inputImageUrls, setInputImageUrls] = useState<string[]>([]);
    const [backgroundImageUrl, setBackgroundImageUrl] = useState<string>('');
    const [customBackgroundUrl, setCustomBackgroundUrl] = useState<string>('');
    const [isProcessing, setIsProcessing] = useState(false);
    const [generatedImages, setGeneratedImages] = useState<string[]>(state?.generatedImages || []);
    const [generationTime, setGenerationTime] = useState<number>(0);
    const [timerInterval, setTimerInterval] = useState<NodeJS.Timeout | null>(null);
    const { currentUser, refreshUserCredits, userCredits } = useAuth();

    const [selectedCategory, setSelectedCategory] = useState<string>(state?.category || 'random');
    const [mode, setMode] = useState<string>(state?.mode || 'balanced');
    // Default values - not shown in UI
    const numImages = 1;
    const outputFormat = 'jpeg';
    const [isExample, setIsExample] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);
    const [advancedPrompt, setAdvancedPrompt] = useState('');
    const [showInsufficientCreditsModal, setShowInsufficientCreditsModal] = useState(false);
    const [backgroundPresets, setBackgroundPresets] = useState<BackgroundPreset[]>([]);
    const [loadingBackgrounds, setLoadingBackgrounds] = useState<boolean>(false);
    const [showCustomUpload, setShowCustomUpload] = useState(false);
    const [isPresetsCollapsed, setIsPresetsCollapsed] = useState(false);

    // Credit estimation hook
    const [creditEstimation, estimateCreditCost] = useCreditEstimation('background-change');

    // Mode restrictions hook
    const modeRestrictions = useModeRestrictions();

    // Effect to handle recreation state
    useEffect(() => {
        const loadInputImages = async () => {
            if (state?.inputImages && state.inputImages.length >= 1) {
                try {
                    setInputImageUrls([state.inputImages[0]]);
                    if (state.generatedImages) {
                        setGeneratedImages(state.generatedImages);
                    }
                } catch (error) {
                    console.error('Failed to load input images:', error);
                }
            }
        };

        loadInputImages();
    }, [state]);

    // Clear error messages when user uploads new images
    useEffect(() => {
        if (inputImageUrls.length > 0) {
            setErrorMessage('');
        }
    }, [inputImageUrls]);

    // Cleanup timer on unmount
    useEffect(() => {
        return () => {
            if (timerInterval) {
                clearInterval(timerInterval);
            }
        };
    }, [timerInterval]);

    // Handle mode restrictions
    useEffect(() => {
        if (modeRestrictions.hasInitialLoad && !modeRestrictions.canUseMode(mode)) {
            const availableMode = modeRestrictions.availableModes.find(m => !m.disabled);
            if (availableMode) {
                setMode(availableMode.value);
            }
        }
    }, [modeRestrictions.hasInitialLoad, modeRestrictions.availableModes, mode, modeRestrictions]);

    // Credit estimation effect
    useEffect(() => {
        const finalBackgroundUrl = customBackgroundUrl || backgroundImageUrl;
        if (!currentUser?.uid || inputImageUrls.length === 0 || !finalBackgroundUrl) {
            return;
        }

        const estimateCost = async () => {
            const finalBackgroundUrl = customBackgroundUrl || backgroundImageUrl;
            const estimationPayload = {
                inputImageUrl: inputImageUrls[0],
                backgroundImageUrl: finalBackgroundUrl,
                userId: currentUser.uid,
                mode: mode.toLowerCase(),
                category: selectedCategory,
                outputFormat: outputFormat,
                numImages: numImages,
                prompt: advancedPrompt || ''
            };

            estimateCreditCost(estimationPayload);
        };

        estimateCost();
    }, [
        currentUser?.uid,
        inputImageUrls,
        backgroundImageUrl,
        customBackgroundUrl,
        selectedCategory,
        mode,
        advancedPrompt,
        estimateCreditCost
    ]);

    const startTimer = () => {
        setGenerationTime(0);
        const interval = setInterval(() => {
            setGenerationTime(prev => prev + 0.1);
        }, 100);
        setTimerInterval(interval);
    };

    const stopTimer = () => {
        if (timerInterval) {
            clearInterval(timerInterval);
            setTimerInterval(null);
        }
    };

    // Fetch background presets from API
    const fetchBackgroundPresets = async (category: string) => {
        setLoadingBackgrounds(true);
        setBackgroundImageUrl(''); // Reset selection when fetching new backgrounds
        setErrorMessage('');

        try {
            // If "random" is selected, fetch without category filter
            const url = category && category !== 'random'
                ? `${API_CONFIG.GET_BACKGROUND_PRESETS_URL}?category=${category}`
                : API_CONFIG.GET_BACKGROUND_PRESETS_URL;

            const response = await fetch(url);

            if (!response.ok) {
                throw new Error('Failed to fetch background presets');
            }

            const data = await response.json();

            if (data.success && Array.isArray(data.backgrounds)) {
                // Filter only backgrounds with liveStatus: true
                const liveBackgrounds = data.backgrounds.filter((bg: BackgroundPreset) => bg.liveStatus === true);
                setBackgroundPresets(liveBackgrounds);
            } else {
                setBackgroundPresets([]);
            }
        } catch (error) {
            console.error('Failed to fetch background presets:', error);
            setErrorMessage('Failed to load background examples. Please try again.');
            setBackgroundPresets([]);
        } finally {
            setLoadingBackgrounds(false);
        }
    };

    // Fetch backgrounds when category changes
    useEffect(() => {
        fetchBackgroundPresets(selectedCategory);
    }, [selectedCategory]);

    // Handle background image selection from examples
    const handleBackgroundSelect = (backgroundUrl: string) => {
        setBackgroundImageUrl(backgroundUrl);
        // Clear custom background if preset is selected (they're mutually exclusive)
        setCustomBackgroundUrl('');
        setIsPresetsCollapsed(true); // Collapse after selection
    };

    const handleGenerate = async () => {
        if (inputImageUrls.length === 0) {
            alert('Please upload a model image');
            return;
        }

        const finalBackgroundUrl = customBackgroundUrl || backgroundImageUrl;
        if (!finalBackgroundUrl) {
            alert('Please select or upload a background image');
            return;
        }

        // Check if user can afford the generation
        if (!creditEstimation.canAfford) {
            setShowInsufficientCreditsModal(true);
            return;
        }

        setIsProcessing(true);
        setGeneratedImages([]);
        setIsExample(false);
        setErrorMessage('');
        startTimer();

        try {
            // Use custom background if available, otherwise use preset
            const finalBackgroundUrl = customBackgroundUrl || backgroundImageUrl;

            const payload = {
                inputImageUrl: inputImageUrls[0],
                backgroundImageUrl: finalBackgroundUrl,
                userId: currentUser?.uid || "user_id",
                mode: mode.toLowerCase(),
                category: selectedCategory,
                outputFormat: outputFormat,
                numImages: numImages,
                prompt: advancedPrompt || ''
            };

            const response = await fetch(API_CONFIG.BACKGROUND_CHANGE_URL, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            const data = await response.json();

            if (!response.ok) {
                if (response.status === 402) {
                    setErrorMessage('Insufficient credits. Please upgrade your plan.');
                    setShowInsufficientCreditsModal(true);
                } else {
                    setErrorMessage(data.message || data.detail || 'Something went wrong. Please try again.');
                }
                return;
            }

            if (data.success && Array.isArray(data.imageUrls) && data.imageUrls.length > 0) {
                setGeneratedImages(data.imageUrls);
                await refreshUserCredits();
                setErrorMessage('');
            } else {
                setErrorMessage(data.message || 'Something went wrong. Please try again.');
            }
        } catch (error) {
            console.error('Failed to change background:', error);
            setErrorMessage('Failed to change background. Please try again.');
        } finally {
            stopTimer();
            setIsProcessing(false);
        }
    };

    // Example handling
    const handleModelImageExampleSelect = (modelImageUrl: string, modelImageId: number) => {
        setIsExample(true);

        const matchingPreset = backgroundChangeExamplePresets.find(
            (preset: BackgroundChangeExamplePreset) => preset.clothPreview === modelImageUrl
        );
        if (matchingPreset) {
            setGeneratedImages(matchingPreset.results);
        }
    };

    return (
        <div className="w-full h-[calc(100vh-120px)] relative">
            {/* Main Content Area - Three Column Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_2fr_2fr] gap-2 p-2 h-full">
                {/* First Column - Model Image Upload and Custom Background */}
                <div className="bg-white rounded-lg shadow-sm border border-accent-taupe flex flex-col h-full">
                    <div className="p-4 border-b border-accent-taupe flex-shrink-0">
                        <h2 className="text-lg font-semibold text-text-heading">Model Image</h2>
                        <span className="text-sm text-text-description">Upload a model image to change background</span>
                    </div>

                    <div className="p-4 flex flex-col flex-1 overflow-y-auto gap-3 min-h-0">
                        {/* Model Image Upload - Fixed Height */}
                        <div className="h-[250px] max-h-[250px] flex-shrink-0 overflow-hidden">
                            <ModelImageSelection
                                imageUrls={inputImageUrls}
                                setImageUrls={setInputImageUrls}
                                isExample={isExample}
                                onExampleSelect={handleModelImageExampleSelect}
                            />
                        </div>

                        {/* Custom Background Upload - Toggleable and Smaller */}
                        <div className="flex-shrink-0">
                            <button
                                onClick={() => setShowCustomUpload(!showCustomUpload)}
                                className="flex items-center justify-between w-full px-3 py-2 bg-accent-sand border border-accent-taupe rounded-lg text-xs font-medium text-text-heading hover:bg-accent-sand transition-colors mb-2"
                            >
                                <span className="flex items-center">
                                    <i className="fas fa-file-upload mr-2 text-secondary-500"></i>
                                    {customBackgroundUrl ? 'Custom Background Uploaded' : 'Upload Custom Background (Optional)'}
                                </span>
                                <i className={`fas fa-chevron-${showCustomUpload || customBackgroundUrl ? 'up' : 'down'} text-text-description`}></i>
                            </button>

                            {(showCustomUpload || customBackgroundUrl) && (
                                <div className="h-[120px] max-h-[120px] overflow-hidden">
                                    <CustomBackgroundUpload
                                        backgroundImageUrl={customBackgroundUrl}
                                        setBackgroundImageUrl={(url) => {
                                            setCustomBackgroundUrl(url);
                                            // Clear preset selection when custom background is uploaded
                                            if (url) {
                                                setBackgroundImageUrl('');
                                            }
                                        }}
                                    />
                                </div>
                            )}
                        </div>

                        {/* Examples Section - At the bottom */}
                        <div className="flex-shrink-0 pt-4 border-t border-accent-taupe">
                            <h4 className={`text-xs font-medium mb-2 ${inputImageUrls.length > 0 ? 'text-text-description' : 'text-text-heading'}`}>
                                Or try an example:
                            </h4>
                            <div className="grid grid-cols-3 gap-2">
                                {exampleModelImages.map((item: { id: number; url: string; description: string }) => (
                                    <button
                                        key={item.id}
                                        onClick={() => handleExampleModelClick(
                                            item.url,
                                            setInputImageUrls,
                                            handleModelImageExampleSelect
                                        )}
                                        disabled={inputImageUrls.length > 0}
                                        className={`group relative rounded-lg overflow-hidden ${inputImageUrls.length > 0
                                            ? 'opacity-50 cursor-not-allowed'
                                            : 'cursor-pointer'
                                            }`}
                                    >
                                        <img
                                            src={item.url}
                                            alt={item.description}
                                            className={`w-full h-32 object-contain transition-opacity ${inputImageUrls.length > 0
                                                ? 'opacity-50 grayscale'
                                                : 'group-hover:opacity-75'
                                                }`}
                                        />
                                        <div className={`absolute inset-0 transition-opacity ${inputImageUrls.length > 0
                                            ? 'bg-accent-sand0 bg-opacity-20'
                                            : 'bg-black bg-opacity-0 group-hover:bg-opacity-20'
                                            }`} />
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Second Column - Controls */}
                <div className="bg-white rounded-lg shadow-sm border border-accent-taupe flex flex-col h-full">
                    <div className="p-4 border-b border-accent-taupe flex-shrink-0">
                        <h2 className="text-lg font-semibold text-text-heading">Controls</h2>
                        <span className="text-sm text-text-description">Configure your background settings</span>
                    </div>

                    <div className="p-4 space-y-3 flex-1 overflow-y-auto">
                        {/* Compact Controls Grid */}
                        <div className="space-y-3">
                            {/* First Row - Background Category and Mode */}
                            <div className="grid grid-cols-2 gap-2">
                                <div>
                                    <label className="block text-xs font-medium text-text-heading mb-1">
                                        Background
                                    </label>
                                    <CustomSelect
                                        value={selectedCategory}
                                        onChange={(value) => setSelectedCategory(value)}
                                        options={BACKGROUND_CATEGORIES.map((option) => ({
                                            value: option.value,
                                            label: option.label
                                        }))}
                                        className="w-full"
                                    />
                                </div>

                                <div>
                                    <label className="block text-xs font-medium text-text-heading mb-1">
                                        Mode
                                        {!modeRestrictions.hasInitialLoad && (
                                            <span className="ml-1 text-xs text-text-description">(Loading...)</span>
                                        )}
                                    </label>
                                    <CustomSelect
                                        value={mode}
                                        onChange={(value) => setMode(value)}
                                        options={modeRestrictions.availableModes.map((modeOption) => {
                                            let planRequirement = '';
                                            if (modeOption.disabled && modeOption.requiresPlan) {
                                                const minPlan = modeOption.requiresPlan[0];
                                                switch (minPlan) {
                                                    case 'plus':
                                                        planRequirement = ' (Plus)';
                                                        break;
                                                    case 'pro':
                                                        planRequirement = ' (Pro)';
                                                        break;
                                                    case 'enterprise':
                                                        planRequirement = ' (Ent)';
                                                        break;
                                                    default:
                                                        planRequirement = ' (Upgrade)';
                                                }
                                            }
                                            return {
                                                value: modeOption.value,
                                                label: `${modeOption.label}${planRequirement}`,
                                                disabled: modeOption.disabled
                                            };
                                        })}
                                        disabled={!modeRestrictions.hasInitialLoad}
                                        className="w-full"
                                    />
                                    {modeRestrictions.error && (
                                        <p className="text-xs text-red-600 mt-1">{modeRestrictions.error}</p>
                                    )}
                                    {!modeRestrictions.canUseMode(mode) && modeRestrictions.hasInitialLoad && (
                                        <p className="text-xs text-orange-600 mt-1">
                                            <i className="fas fa-exclamation-triangle mr-1"></i>
                                            {modeRestrictions.getRestrictedModeMessage(mode)}
                                        </p>
                                    )}
                                </div>
                            </div>

                            {/* Background Selection */}
                            <div>
                                <label className="block text-xs font-medium text-text-heading mb-1">
                                    Select Background
                                    {customBackgroundUrl && (
                                        <span className="ml-1 text-xs text-text-description">(Disabled - Custom background uploaded)</span>
                                    )}
                                </label>
                                <div className={`bg-accent-sand rounded-lg p-2 border border-accent-taupe ${isPresetsCollapsed && backgroundImageUrl ? '' : 'min-h-[300px] max-h-[300px] md:min-h-[180px] md:max-h-[200px] lg:max-h-[300px] xl:max-h-[400px] 2xl:max-h-[500px]'} overflow-y-auto ${customBackgroundUrl ? 'opacity-50 pointer-events-none' : ''}`}>
                                    {isPresetsCollapsed && backgroundImageUrl ? (
                                        <div className="flex items-center justify-between bg-white p-2 rounded-md border border-accent-taupe">
                                            <div className="flex items-center space-x-3">
                                                <img
                                                    src={backgroundImageUrl}
                                                    alt="Selected background"
                                                    className="w-12 h-12 object-cover rounded-md"
                                                />
                                                <div>
                                                    <p className="text-xs font-medium text-text-heading">Background Selected</p>
                                                    <p className="text-[10px] text-text-description">Preset background</p>
                                                </div>
                                            </div>
                                            <button
                                                onClick={() => setIsPresetsCollapsed(false)}
                                                className="text-secondary-600 hover:text-secondary-800 text-xs font-medium px-2 py-1 rounded hover:bg-secondary-100 transition-colors"
                                            >
                                                Change
                                            </button>
                                        </div>
                                    ) : loadingBackgrounds ? (
                                        <div className="flex items-center justify-center py-8">
                                            <div className="text-center">
                                                <span className="inline-block w-6 h-6 border-2 border-secondary-500 border-t-transparent rounded-full animate-spin mb-2"></span>
                                                <p className="text-xs text-text-description">Loading backgrounds...</p>
                                            </div>
                                        </div>
                                    ) : backgroundPresets.length > 0 ? (
                                        <>
                                            <div className="grid grid-cols-3 gap-2">
                                                {backgroundPresets.map((bg) => (
                                                    <button
                                                        key={bg.backgroundId}
                                                        onClick={() => handleBackgroundSelect(bg.imageUrl)}
                                                        disabled={!!customBackgroundUrl}
                                                        className={`relative aspect-square rounded-md overflow-hidden border-2 transition-all ${customBackgroundUrl
                                                            ? 'border-accent-taupe cursor-not-allowed opacity-50'
                                                            : backgroundImageUrl === bg.imageUrl
                                                                ? 'border-secondary-500 ring-2 ring-secondary-200'
                                                                : 'border-accent-taupe hover:border-accent-taupe'
                                                            }`}
                                                    >
                                                        <img
                                                            src={bg.imageUrl}
                                                            alt={`${bg.category} background ${bg.backgroundId}`}
                                                            className="w-full h-full object-cover"
                                                            onError={(e) => {
                                                                // Fallback if image fails to load
                                                                (e.target as HTMLImageElement).src = 'https://via.placeholder.com/200?text=Background';
                                                            }}
                                                        />
                                                        {backgroundImageUrl === bg.imageUrl && !customBackgroundUrl && (
                                                            <div className="absolute inset-0 bg-secondary-1000 bg-opacity-20 flex items-center justify-center">
                                                                <svg className="w-6 h-6 text-secondary-600" fill="currentColor" viewBox="0 0 20 20">
                                                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                                </svg>
                                                            </div>
                                                        )}
                                                        <div className="absolute bottom-1 right-1 bg-black/40 text-white text-[10px] px-1 rounded backdrop-blur-sm pointer-events-none uppercase tracking-wider font-semibold">
                                                            {bg.category}
                                                        </div>
                                                    </button>
                                                ))}
                                            </div>
                                            {(backgroundImageUrl || customBackgroundUrl) && (
                                                <p className="text-xs text-text-description mt-2 text-center">
                                                    {customBackgroundUrl ? 'Custom background uploaded' : 'Background selected'}
                                                </p>
                                            )}
                                        </>
                                    ) : (
                                        <div className="flex items-center justify-center py-8">
                                            <div className="text-center">
                                                <p className="text-xs text-text-description">No backgrounds available for this category</p>
                                                <button
                                                    onClick={() => fetchBackgroundPresets(selectedCategory)}
                                                    className="text-xs text-secondary-600 hover:text-secondary-700 mt-2 underline"
                                                    disabled={!!customBackgroundUrl}
                                                >
                                                    Try again
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Advanced Settings */}
                        <div className="border-t border-accent-taupe pt-2">
                            <button
                                type="button"
                                onClick={() => setShowAdvancedSettings(!showAdvancedSettings)}
                                className="flex items-center justify-between w-full text-left text-xs font-medium text-text-heading hover:text-text-heading transition-colors"
                            >
                                <span className="flex items-center">
                                    <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                    </svg>
                                    Advanced Settings
                                </span>
                                <svg
                                    className={`w-3 h-3 transition-transform duration-200 ${showAdvancedSettings ? 'rotate-180' : ''}`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                            </button>

                            {showAdvancedSettings && (
                                <div className="mt-2">
                                    <div>
                                        <label className="block text-xs font-medium text-text-heading mb-1">Prompt</label>
                                        <textarea
                                            value={advancedPrompt}
                                            onChange={(e) => setAdvancedPrompt(e.target.value)}
                                            placeholder="Add specific instructions for background generation (e.g., modern office with natural lighting)"
                                            className="w-full border border-accent-taupe rounded-lg px-2 py-2 text-sm focus:ring-secondary-500 focus:border-secondary-500 min-h-[60px] resize-y"
                                        />
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Credit Cost Preview */}
                        <div className="bg-accent-sand rounded-lg p-2">
                            <CreditCostPreview
                                creditCost={creditEstimation.creditCost}
                                isEstimating={creditEstimation.isEstimating}
                                error={creditEstimation.error}
                                canAfford={creditEstimation.canAfford}
                                className="text-xs"
                            />
                        </div>

                        {/* Clear Button */}
                        <button
                            onClick={() => {
                                setSelectedCategory('random');
                                setMode('performance');
                                setBackgroundImageUrl('');
                                setCustomBackgroundUrl('');
                                setAdvancedPrompt('');
                                setGeneratedImages([]);
                                setIsExample(false);
                                setErrorMessage('');
                                setInputImageUrls([]);
                                setShowCustomUpload(false);
                                setIsPresetsCollapsed(false);
                            }}
                            className="w-full px-4 py-2.5 rounded-lg font-medium text-sm transition-colors bg-red-100 hover:bg-red-200 text-red-700 border border-red-300"
                            title="Clear all settings"
                        >
                            Clear All Settings
                        </button>

                        {/* Generate Button */}
                        <button
                            onClick={handleGenerate}
                            disabled={inputImageUrls.length === 0 || (!backgroundImageUrl && !customBackgroundUrl) || isProcessing}
                            className={`w-full px-4 py-2.5 rounded-lg font-medium text-sm transition-colors ${inputImageUrls.length === 0 || (!backgroundImageUrl && !customBackgroundUrl) || isProcessing
                                ? 'bg-gray-300 text-text-description cursor-not-allowed'
                                : 'bg-secondary-500 hover:bg-secondary-600 text-white'
                                }`}
                        >
                            {isProcessing ? (
                                <div className="flex items-center justify-center space-x-2">
                                    <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                                    <span>Processing...</span>
                                    <div className="flex items-center space-x-1 opacity-90">
                                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                        <span className="font-mono font-semibold text-xs">
                                            {generationTime.toFixed(1)}s
                                        </span>
                                    </div>
                                </div>
                            ) : (
                                'Change Background'
                            )}
                        </button>

                        {/* Error message display */}
                        {errorMessage && (
                            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                                <div className="flex items-center">
                                    <svg className="w-4 h-4 text-red-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    <p className="text-xs text-red-700">{errorMessage}</p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Third Column - Generated Result */}
                <div className="bg-white rounded-lg shadow-sm border border-accent-taupe flex flex-col h-full">
                    <div className="p-4 border-b border-accent-taupe flex-shrink-0">
                        <h2 className="text-lg font-semibold text-text-heading">Generated Result</h2>
                        <span className="text-sm text-text-description">
                            {isExample ? 'Example result will appear here' : 'Generated images will appear here'}
                        </span>
                    </div>

                    <div className="p-4 flex-1 flex flex-col overflow-y-auto min-h-0">
                        {isProcessing ? (
                            <div className="flex-1 bg-accent-sand rounded-lg border border-accent-taupe flex items-center justify-center min-h-0">
                                <div className="text-center">
                                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-secondary-500 mx-auto mb-4"></div>
                                    <p className="text-sm text-text-description">Changing background...</p>
                                    <p className="text-xs text-text-description mt-2">This may take a few moments</p>
                                </div>
                            </div>
                        ) : generatedImages.length > 0 ? (
                            <div className="relative flex-1 overflow-y-auto min-h-0">
                                <div className={`${generatedImages.length === 1 ? 'grid grid-cols-1' : 'grid grid-cols-1 sm:grid-cols-2'} gap-2`}>
                                    {generatedImages.map((url, idx) => (
                                        <div key={idx} className="relative bg-accent-sand rounded-lg border border-accent-taupe overflow-hidden flex flex-col min-h-[300px]">
                                            <img
                                                src={url}
                                                alt={`${isExample ? 'Example' : 'Generated'} ${idx + 1}`}
                                                className="w-full h-[300px] object-contain"
                                            />
                                            <div className="absolute top-1 left-1 bg-secondary-500 text-white text-xs font-semibold px-1.5 py-0.5 rounded">
                                                #{idx + 1}
                                            </div>
                                            {/* Action bar */}
                                            <div className="flex justify-center items-center gap-2 px-2 py-2 bg-white border-t border-accent-taupe">
                                                {/* Download Button */}
                                                <button
                                                    className="text-text-description hover:text-secondary-600 hover:bg-secondary-100 border border-accent-taupe hover:border-secondary-300 px-2 py-1 rounded-lg transition-colors text-xs font-medium"
                                                    onClick={async () => {
                                                        try {
                                                            const response = await fetch(url);
                                                            const blob = await response.blob();
                                                            const blobUrl = window.URL.createObjectURL(blob);
                                                            const link = document.createElement('a');
                                                            link.href = blobUrl;
                                                            link.download = `${isExample ? 'example' : 'background-change'}-result-${idx + 1}.jpg`;
                                                            link.style.display = 'none';
                                                            document.body.appendChild(link);
                                                            link.click();
                                                            document.body.removeChild(link);
                                                            window.URL.revokeObjectURL(blobUrl);
                                                        } catch (error) {
                                                            console.error('Download failed:', error);
                                                        }
                                                    }}
                                                >
                                                    Download
                                                </button>
                                                {/* Upscale Button */}
                                                <button
                                                    className="text-text-description hover:text-secondary-600 hover:bg-secondary-100 border border-accent-taupe hover:border-secondary-300 px-2 py-1 rounded-lg transition-colors text-xs font-medium"
                                                    onClick={() => {
                                                        navigate('/canvas-editor', {
                                                            state: {
                                                                inputImages: [url],
                                                                prompt: '',
                                                                style: '',
                                                                variations: 1,
                                                                recreatingFrom: '',
                                                                tool: 'upscaler'
                                                            }
                                                        });
                                                    }}
                                                >
                                                    HD
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ) : (
                            <div className="flex-1 bg-accent-sand rounded-lg border border-accent-taupe flex items-center justify-center min-h-0">
                                <div className="text-center text-text-description">
                                    <svg className="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                    </svg>
                                    <p className="text-sm">Upload a model image and select a background category to get started</p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Insufficient Credits Modal */}
            <InsufficientCreditsModal
                isOpen={showInsufficientCreditsModal}
                onClose={() => setShowInsufficientCreditsModal(false)}
                requiredCredits={creditEstimation.creditCost || 0}
                availableCredits={userCredits}
                featureName="Background Change"
            />
        </div>
    );
};

export default AIBackgroundChange;
